import type { Card, Stage } from './api';

/**
 * The director's morning (D7): what the board says before anyone opens a
 * card. Pure over the cards, the render jobs and the scheduled publications,
 * so the wording is tested; the deck draws it as one line of chips.
 */
export interface BriefRender { status: string; card_id: string | null; error?: string | null; finished_at?: string | null }
export interface BriefPublication { status: string; scheduled_for: string | null; card_title: string; platform: string; published_at?: string | null; views?: number | null }
export interface BriefItem { kind: 'phase' | 'stale' | 'failed' | 'next' | 'best' | 'quiet' | 'live'; text: string; stage?: Stage; cardId?: string | null; roomId?: string }
/** D21: what the last live left behind — from the room's debrief. */
export interface BriefLive { id: string; name: string; debriefed_at: string | null; followUps: number; asked: number; nextTitle: string }

export const STALE_DAYS = 7;

export function brief(cards: Card[], renders: BriefRender[], pubs: BriefPublication[], now: Date = new Date(), lives: BriefLive[] = []): BriefItem[] {
  const out: BriefItem[] = [];
  // D21: the last live's debrief, while it is fresh (two weeks) and its next episode is not on the board yet.
  const live = lives.filter((l) => l.debriefed_at && now.getTime() - new Date(l.debriefed_at).getTime() < 14 * 86_400_000)
    .sort((a, b) => (a.debriefed_at! < b.debriefed_at! ? 1 : -1))[0];
  if (live && !cards.some((c) => live.nextTitle && c.title === live.nextTitle)) {
    const bits = [live.followUps ? `${live.followUps} follow-up${live.followUps === 1 ? '' : 's'}` : null, live.asked ? `${live.asked} asked` : null].filter(Boolean).join(', ');
    out.push({ kind: 'live', roomId: live.id, text: `from your last live (${short(live.name)}): ${bits || 'the debrief'}${live.nextTitle ? ` · next: ${short(live.nextTitle)}` : ''}` });
  }
  const inFlight: Stage[] = ['idea', 'scripted', 'filming', 'editing', 'review'];
  for (const stage of inFlight) {
    const n = cards.filter((c) => c.stage === stage).length;
    if (n) out.push({ kind: 'phase', stage, text: `${n} in ${stage === 'idea' ? 'Idea' : stage[0]!.toUpperCase() + stage.slice(1)}` });
  }
  const failed = renders.filter((r) => r.status === 'failed' && r.card_id);
  if (failed.length) {
    const first = failed[0]!;
    const title = cards.find((c) => c.id === first.card_id)?.title;
    out.push({ kind: 'failed', cardId: first.card_id, text: failed.length === 1
      ? `a render failed${title ? ` — ${short(title)}` : ''}`
      : `${failed.length} renders failed` });
  }
  const cutoff = now.getTime() - STALE_DAYS * 86_400_000;
  const stale = cards.filter((c) => inFlight.includes(c.stage) && new Date(c.updated_at ?? c.created_at).getTime() < cutoff);
  if (stale.length) {
    out.push({ kind: 'stale', cardId: stale[0]!.id, text: stale.length === 1
      ? `${short(stale[0]!.title)} has not moved in a week`
      : `${stale.length} cards have not moved in a week` });
  }
  const next = pubs
    .filter((p) => p.status === 'queued' && p.scheduled_for && new Date(p.scheduled_for).getTime() >= now.getTime())
    .sort((a, b) => new Date(a.scheduled_for!).getTime() - new Date(b.scheduled_for!).getTime())[0];
  if (next) out.push({ kind: 'next', text: `next out: ${short(next.card_title)} · ${when(new Date(next.scheduled_for!), now)} on ${next.platform}` });
  // D11: the week's best, by what the audience did.
  const week = now.getTime() - 7 * 86_400_000;
  const best = pubs
    .filter((p) => p.status === 'published' && typeof p.views === 'number' && p.published_at && new Date(p.published_at).getTime() >= week)
    .sort((a, b) => (b.views ?? 0) - (a.views ?? 0))[0];
  if (best) out.push({ kind: 'best', text: `best this week: ${short(best.card_title)} · ${(best.views ?? 0).toLocaleString()} views` });
  if (!out.length) out.push({ kind: 'quiet', text: cards.length ? 'nothing in flight — start the next episode' : 'the board is empty — start with Hooks & SEO' });
  return out;
}

function short(title: string): string {
  return title.length > 42 ? `${title.slice(0, 41).trimEnd()}…` : title;
}

export function when(at: Date, now: Date): string {
  const days = Math.round((startOfDay(at) - startOfDay(now)) / 86_400_000);
  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (days === 0) return `today ${time}`;
  if (days === 1) return `tomorrow ${time}`;
  if (days < 7) return `${at.toLocaleDateString([], { weekday: 'long' })} ${time}`;
  return at.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
